import { ChevronDownIcon } from '@heroicons/react/24/outline';
import classNames from 'classnames';
import { ColumnButton } from 'lib/components/ColumnButton';
import { Popover, PopoverContext } from 'lib/components/Popover';
import { PublicationIssueModel } from 'lib/model/objects/publicationIssueModel';
import { PublicationIssueSectionModel } from 'lib/model/objects/publicationIssueSectionModel';
import { safeAsync } from 'lib/safeWrappers';
import { ColumnService } from 'lib/services/directory';
import { useContext, useEffect, useState } from 'react';
import { logAndCaptureException, logAndCaptureMessage } from 'utils';
import { getFirebaseContext } from 'utils/firebase';
import { Paginator } from './hooks/useAvailablePaginators';

type PaginatorSelectProps = {
  availablePaginators: Paginator[];
  updatePaginator: (id: string) => void;
};

function PaginatorSelect({
  availablePaginators,
  updatePaginator
}: PaginatorSelectProps) {
  const { setOpen } = useContext(PopoverContext);
  return (
    <div className="w-64 space-y-2 p-4">
      {availablePaginators.map(paginator => {
        return (
          <div key={`wrapper-${paginator.id}`} className="w-full flex">
            <ColumnButton
              key={`${paginator.id}`}
              type={'button'}
              link
              buttonText={paginator.name}
              onClick={() => {
                void updatePaginator(paginator.id);
                setOpen(false);
              }}
            />
          </div>
        );
      })}
    </div>
  );
}

type PaginatorBadgeProps = {
  controllingDocument: PublicationIssueModel | PublicationIssueSectionModel;
  availablePaginators: Paginator[];
};

export default function PaginatorBadge({
  controllingDocument,
  availablePaginators
}: PaginatorBadgeProps) {
  const [paginator, setPaginator] = useState<Paginator>();

  const updatePaginator = async (id: string) => {
    const newPaginator = availablePaginators.find(p => p.id === id);
    if (!newPaginator) {
      logAndCaptureMessage('Selected paginator not available', {
        selectedPagintorId: id,
        currentPaginatorId: paginator?.id,
        currentPaginatorName: paginator?.name
      });
      return;
    }
    const [updateError] = await safeAsync(() =>
      controllingDocument.ref.update({
        paginator: getFirebaseContext().usersRef().doc(id)
      })
    )();
    if (updateError) {
      logAndCaptureException(
        ColumnService.PAGINATION,
        updateError,
        'Failed to update assigned paginator to issue or section',
        {
          docPath: controllingDocument.ref.path,
          newPaginatorId: id
        }
      );
      return;
    }
    setPaginator(newPaginator);
  };

  useEffect(() => {
    const initialPaginatorId = controllingDocument.modelData.paginator?.id;
    const initialPaginator = availablePaginators.find(
      p => p.id === initialPaginatorId
    );
    if (initialPaginator) {
      setPaginator(initialPaginator);
    }
  }, [controllingDocument.id, availablePaginators]);

  return (
    <div
      onClick={(e: React.MouseEvent) => {
        // Prevent the drawer from opening
        e.stopPropagation();
      }}
    >
      <Popover
        id="paginator-badge-popover"
        activator={
          <button
            className={classNames(
              'px-2 py-1 flex space-x-1 cursor-pointer rounded-full text-xs font-medium',
              `bg-${paginator?.color}-700 text-${paginator?.color}-100`
            )}
          >
            <span>{paginator?.name || 'Unassigned'}</span>
            <span className="m-auto">
              <ChevronDownIcon className="w-3 h-3" />
            </span>
          </button>
        }
      >
        <PaginatorSelect
          availablePaginators={availablePaginators}
          updatePaginator={updatePaginator}
        />
      </Popover>
    </div>
  );
}
